/**
 * WorkspaceContext — structured contract for NEXUS Workspace Intelligence Layer.
 * Every admin page should register context that normalizes into this shape.
 */

import { getAdminUser } from "../api/adminClient";
import { buildAssistantAllowedActions } from "../admin/assistantAllowedActions";
import { ROUTES } from "../admin/routes";

export const WORKSPACE_CONTEXT_VERSION = 1;

const CLASSIC_DESIGNATIONS = ["CEO", "Leadership Team", "Board Member", "PE Sponsor"];

/** Server-enriched identity from GET /workspace (onboarding, sections). */
let serverWorkspaceIdentity = null;
const serverWorkspaceIdentityListeners = new Set();

export function subscribeServerWorkspaceIdentity(listener) {
  serverWorkspaceIdentityListeners.add(listener);
  return () => serverWorkspaceIdentityListeners.delete(listener);
}

function notifyServerWorkspaceIdentity() {
  serverWorkspaceIdentityListeners.forEach((listener) => {
    try {
      listener();
    } catch {
      /* ignore */
    }
  });
}

export function applyServerWorkspaceIdentity(patch) {
  if (!patch || typeof patch !== "object") {
    serverWorkspaceIdentity = null;
    notifyServerWorkspaceIdentity();
    return;
  }
  serverWorkspaceIdentity = { ...patch };
  notifyServerWorkspaceIdentity();
}

export function clearServerWorkspaceIdentity() {
  serverWorkspaceIdentity = null;
  notifyServerWorkspaceIdentity();
}

/** Shape expected by workspaceLiteInviteAvailable (session + GET /workspace). */
export function buildWorkspaceProductDoc(user = getAdminUser()) {
  const server = serverWorkspaceIdentity;
  if (!user?.tenant_id || user.is_platform_admin) {
    return {
      id: null,
      email_pack: server?.email_pack || user?.email_pack || "5x",
      lite_invite_available: server?.lite_invite_available,
      product_settings: server?.product_settings,
    };
  }
  return {
    id: String(user.tenant_id),
    email_pack: server?.email_pack || user.email_pack || "generic",
    lite_invite_available:
      server?.lite_invite_available ?? user.lite_invite_available,
    product_settings:
      server?.product_settings ||
      (typeof user.product_settings === "object" ? user.product_settings : undefined),
  };
}

/** Merge GET /workspace payload into the cached server identity (NEXUS + settings UI). */
export function mergeWorkspaceApiIntoServerIdentity(ws, user, extras = {}) {
  if (!ws || !user?.tenant_id) return;
  const designationLabels = Array.isArray(ws.designations)
    ? ws.designations.map((d) => (typeof d === "string" ? d : d?.label)).filter(Boolean)
    : [];
  applyServerWorkspaceIdentity({
    tenant_id: String(user.tenant_id),
    tenant_name: ws.name || user.tenant_name || null,
    product_display_name: ws.product_display_name || ws.name || user.product_display_name,
    email_pack: ws.email_pack || user.email_pack || "generic",
    is_classic: false,
    designations: designationLabels,
    lite_invite_available: ws.lite_invite_available,
    product_settings: ws.product_settings,
    ...extras,
  });
}

/**
 * Active workspace identity from the signed-in admin session.
 * Classic (no tenant_id) → 5X defaults; tenant → product/pack/designations.
 */
export function buildWorkspaceIdentity(user = getAdminUser()) {
  if (!user || !user.tenant_id || user.is_platform_admin) {
    const classic = {
      tenant_id: null,
      tenant_name: null,
      product_display_name: "5x Team Assessment",
      email_pack: "5x",
      is_classic: true,
      scoring_model: "five_x",
      designations: CLASSIC_DESIGNATIONS,
      assessment_format: {
        key: "5x_v1",
        name: "5X Team Assessment",
        is_system: true,
      },
      sections: [],
    };
    return classic;
  }
  const pack = user.email_pack || "generic";
  const product = (
    user.product_display_name ||
    serverWorkspaceIdentity?.product_display_name ||
    user.tenant_name ||
    serverWorkspaceIdentity?.tenant_name ||
    "Team Assessment"
  ).trim();
  const tenantBase = {
    tenant_id: String(user.tenant_id),
    tenant_name: user.tenant_name || null,
    product_display_name: product,
    email_pack: pack,
    is_classic: false,
    scoring_model: pack === "5x" ? "five_x" : "generic",
    designations: Array.isArray(user.designations)
      ? user.designations.map((d) => (typeof d === "string" ? d : d?.label)).filter(Boolean)
      : [],
    assessment_format: user.assessment_format || {
      key: null,
      name: null,
      is_system: false,
    },
    sections: Array.isArray(user.assessment_sections) ? user.assessment_sections : [],
  };
  return mergeServerIdentity(tenantBase);
}

function mergeServerIdentity(base) {
  if (
    !serverWorkspaceIdentity ||
    !base?.tenant_id ||
    String(serverWorkspaceIdentity.tenant_id || "") !== String(base.tenant_id)
  ) {
    return base;
  }
  return {
    ...base,
    ...serverWorkspaceIdentity,
    product_display_name:
      serverWorkspaceIdentity.product_display_name || base.product_display_name,
    designations:
      (serverWorkspaceIdentity.designations?.length
        ? serverWorkspaceIdentity.designations
        : base.designations) || [],
    sections:
      (serverWorkspaceIdentity.sections?.length ? serverWorkspaceIdentity.sections : base.sections) ||
      [],
  };
}

/** Normalize focused company from page builders (wizard, insights, filters). */
export function deriveSelectedCompany(pageContext) {
  if (!pageContext || typeof pageContext !== "object") return null;

  const direct = pageContext.selectedCompany;
  if (direct && typeof direct === "object") {
    const name =
      direct.name ||
      direct.company_name ||
      direct.companyName ||
      null;
    if (name) {
      return {
        id: direct.id || direct.company_id || null,
        name: String(name).trim(),
        mode: direct.mode || null,
      };
    }
  }

  const focus = pageContext.focusOrg;
  if (focus && typeof focus === "object" && focus.name) {
    return {
      id: pageContext.focusOrgId || focus.id || null,
      name: String(focus.name).trim(),
      reportReady: focus.reportReady ?? null,
    };
  }

  const wizard = pageContext.wizard;
  if (wizard?.companyName) {
    return {
      id: wizard.selectedCompanyId || null,
      name: String(wizard.companyName).trim(),
      step: wizard.step ?? null,
    };
  }

  const filter = (pageContext.filterText || pageContext.workspace?.filterText || "").trim();
  if (filter && pageContext.page === "company_profile") {
    return { id: null, name: filter, source: "list_filter" };
  }

  return null;
}

/** Wrap page-specific builder output with workspace metadata. */
export function normalizeWorkspaceContext(pageContext) {
  if (!pageContext || typeof pageContext !== "object") return null;

  const route = pageContext.currentRoute || pageContext.pagePath || "";
  const scope = pageContext.scope && typeof pageContext.scope === "object" ? pageContext.scope : {};
  const baseIdentity = buildWorkspaceIdentity();
  const workspaceIdentity =
    pageContext.workspaceIdentity && typeof pageContext.workspaceIdentity === "object"
      ? { ...pageContext.workspaceIdentity, ...baseIdentity }
      : baseIdentity;

  const { allowedActions, actionLabels } =
    pageContext.allowedActions?.length
      ? {
          allowedActions: pageContext.allowedActions,
          actionLabels: pageContext.actionLabels || {},
        }
      : buildAssistantAllowedActions();

  const selectedCompany =
    pageContext.selectedCompany && pageContext.selectedCompany.name
      ? pageContext.selectedCompany
      : deriveSelectedCompany(pageContext);

  const viewState = {
    page: pageContext.page || null,
    surface: pageContext.surface || "",
    route,
    lens: pageContext.activeLens || pageContext.lens || pageContext.filters?.lens || null,
    reportBase: pageContext.reportBase || null,
    editorOpen: pageContext.editorOpen ?? null,
  };

  return {
    ...pageContext,
    workspaceContextVersion: WORKSPACE_CONTEXT_VERSION,
    workspaceIdentity,
    allowedActions,
    actionLabels,
    selectedCompany,
    viewState,
    workspace: {
      pageId: pageContext.page || null,
      route,
      scope,
      filterText: pageContext.filterText || "",
      filters: pageContext.filters && typeof pageContext.filters === "object" ? pageContext.filters : {},
      surface: pageContext.surface || "",
      tenantId: workspaceIdentity.tenant_id,
      productDisplayName: workspaceIdentity.product_display_name,
      scoringModel: workspaceIdentity.scoring_model,
      isClassic: workspaceIdentity.is_classic,
      selectedCompanyName: selectedCompany?.name || null,
    },
    metrics: {
      kpis: pageContext.kpis && typeof pageContext.kpis === "object" ? pageContext.kpis : {},
    },
    entities: extractEntities(pageContext),
    operationalState: extractOperationalState(pageContext),
    summaries: {
      scopeText: scope.text || "",
      hints: Array.isArray(pageContext.hints) ? pageContext.hints : [],
      productName: workspaceIdentity.product_display_name,
    },
  };
}

function extractEntities(ctx) {
  const entities = {};
  for (const key of [
    "companies",
    "organizationRows",
    "organizations",
    "visibleProfiles",
    "participants",
    "draftParticipants",
    "companyRollup",
    "topPerformers",
    "bottomPerformers",
    "compareCompanies",
  ]) {
    if (Array.isArray(ctx[key]) && ctx[key].length) {
      entities[key] = ctx[key].slice(0, 120);
    }
  }
  return entities;
}

function extractOperationalState(ctx) {
  const state = {};
  if (ctx.kpis && typeof ctx.kpis === "object") {
    Object.assign(state, ctx.kpis);
  }
  if (ctx.wizard && typeof ctx.wizard === "object") {
    state.wizardStep = ctx.wizard.step;
    state.wizardReadyToSend = ctx.wizard.readyToSend;
  }
  if (ctx.liteToFull && typeof ctx.liteToFull === "object") {
    state.liteToFullConversionPct = ctx.liteToFull.conversion_pct;
    state.awaitingUpgrade = ctx.liteToFull.awaiting_upgrade;
  }
  return state;
}

/** Merge route metadata and workspace envelope for platform chat. */
export function buildWorkspaceChatContext(pageContext, pathname = ROUTES.dashboard) {
  const merged = {
    ...(pageContext || {}),
    currentRoute: pageContext?.currentRoute || pageContext?.pagePath || pathname || ROUTES.dashboard,
    pagePath: pageContext?.pagePath || pathname || pageContext?.currentRoute || ROUTES.dashboard,
    workspaceIdentity: buildWorkspaceIdentity(),
  };
  return normalizeWorkspaceContext(merged) || merged;
}
